import { computed, effect, inject, ResourceRef } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { QuestionDTO, TypeParcours } from "@mra/hlp9002";
import { tapResponse } from "@ngrx/operators";
import { getState, patchState, signalStore, withComputed, withHooks, withMethods, withState } from "@ngrx/signals";
import { map, Observable } from "rxjs";
import { AffichageInfos } from "src/app/models/affichageInfos";
import { CODE_FINAL_QUESTION, QuestionReponse } from "src/app/models/questionReponse.model";
import { LocalStorageService, StateKey } from "../shared/services/local-storage.service";
import { QuestionnaireService } from "../shared/services/questionnaire.service";
import { SimuUtils } from "../utils/simu-utils";
import { errorsStore } from "./errors.store";
import { ChangementQuestion, QuestionnaireState } from "./state";
import { userStore } from "./user.store";

function createDefaultState(): QuestionnaireState {
  return {
    typeParcours: TypeParcours.SIMPLE,
    questionsReponses: [],
    affichageInfos: null
  };
}

export const questionnaireStore = signalStore(
  { providedIn: "root" },
  withState(createDefaultState()),

  withComputed((state) => ({
    getAllResponses: computed(() => {
      const questRes = state.questionsReponses();
      if (!questRes) return "";
      return questRes
        .filter((res) => res.questionCode !== CODE_FINAL_QUESTION)
        .reduce((acc, res) => {
          if (!res.reponseCode) return acc;
          const separator: string = acc ? "_" : "";
          const numeroQuestion: number = parseInt(res.questionCode?.toLowerCase().replace("q", ""));
          acc += separator + numeroQuestion + res.reponseCode.toLowerCase();
          return acc;
        }, "");
    }),
    currentQuestionReponse: computed(() => state.questionsReponses()?.find((qr) => qr.current))
  })),

  withMethods(
    (
      store,
      questionnaireService = inject(QuestionnaireService),
      user = inject(userStore),
      errors = inject(errorsStore)
    ) => ({
      retrieveState(savedState: QuestionnaireState): void {
        patchState(store, () => savedState ?? createDefaultState());
      },

      unsetSimulation(): void {
        patchState(store, () => createDefaultState());
      },

      changeQuestion(props: ChangementQuestion): void {
        patchState(store, (state) => {
          const oldTableau: QuestionReponse[] = state.questionsReponses ?? [];
          let existingRes: QuestionReponse = null;
          let nouveauTableau: QuestionReponse[] = oldTableau.map((questRes) => {
            //--- enregistre la réponse si la question existe dans l'historique
            if (questRes.questionCode === props.currentQuestionCode) {
              existingRes = { ...questRes };
              questRes.reponseCode = props.currentResponse;
            }
            return { ...questRes, current: false };
          });

          //--- Si la réponse n'existe pas, on l'ajoute à la liste
          if (!existingRes) {
            nouveauTableau.push({
              questionCode: props.currentQuestionCode,
              reponseCode: props.currentResponse,
              current: false
            });
          }
          //--- Sinon, si elle a changé, le déroulé des questions aussi, donc on vide les autres réponses
          else if (existingRes.reponseCode !== props.currentResponse) {
            let questionCode: string = props.currentQuestionCode;
            while (questionCode) {
              const toFind: string = questionCode;
              questionCode = null;
              nouveauTableau = nouveauTableau.filter((oldQuestRes) => {
                if (oldQuestRes.questionPrecedenteCode === toFind) {
                  questionCode = oldQuestRes.questionCode;
                  return false;
                }
                return true;
              });
            }
          }

          //--- passe à la nouvelle question
          const nextQuestDoesntExist: boolean = !nouveauTableau.some(
            (curr) => curr.questionCode === props.nextQuestionCode
          );
          if (props.nextQuestionCode && nextQuestDoesntExist) {
            nouveauTableau.push({
              questionCode: props.nextQuestionCode,
              reponsePrecedente: props.currentResponse,
              questionPrecedenteCode: props.currentQuestionCode,
              reponseCode: null,
              current: true
            });
          } else {
            nouveauTableau = nouveauTableau.map((questRes) => ({
              ...questRes,
              current: questRes.questionCode === props.nextQuestionCode
            }));
          }

          return { questionsReponses: nouveauTableau };
        });
      },

      questionPrecedente(): void {
        patchState(store, (state) => {
          const current: QuestionReponse = state.questionsReponses?.find((curr) => curr.current);
          return {
            questionsReponses: state.questionsReponses?.map((curr) => ({
              ...curr,
              current: curr.questionCode === current.questionPrecedenteCode
            }))
          };
        });
      },

      setAffichageInfos(affichageInfos: AffichageInfos): void {
        patchState(store, () => ({ affichageInfos: affichageInfos }));
      },

      naviguerNext(): void {
        patchState(store, (state) => {
          let newIndex: number = (state.affichageInfos?.current ?? 0) + 1;
          if (newIndex > state.affichageInfos?.ecrans?.length - 1) {
            newIndex = state.affichageInfos?.current ?? 0;
          }
          return changeIndexInState(state, newIndex);
        });
      },

      naviguerPrev(): void {
        patchState(store, (state) => {
          let newIndex: number = state.affichageInfos.current - 1;
          if (newIndex < 0) {
            newIndex = 0;
          }
          return changeIndexInState(state, newIndex);
        });
      },

      getNumeroQuestion: (codeQuestion: string) =>
        computed(() => {
          const questRes = store.questionsReponses();
          if (codeQuestion === CODE_FINAL_QUESTION) return 0;
          let numero: number = 0;
          let currentCode: string = codeQuestion;
          while (currentCode) {
            const question: QuestionReponse = questRes?.find((quest) => quest.questionCode === currentCode);
            currentCode = question?.questionPrecedenteCode;
            numero++;
          }
          return numero;
        }),

      createCurrentQuestionResource: (): ResourceRef<QuestionDTO> => {
        return rxResource({
          request: computed(() => store.currentQuestionReponse() ?? null),
          loader: (params) => {
            const questRes = params.request;
            const dateEffet: string = SimuUtils.getDateEffet(null, user.simulation());
            const questionObs: Observable<QuestionDTO> = questRes?.questionPrecedenteCode
              ? questionnaireService.getNextQuestion(
                  questRes?.questionPrecedenteCode,
                  questRes?.reponsePrecedente,
                  dateEffet,
                  user.epargneCumulee()
                )
              : questionnaireService.getFirstQuestion(dateEffet, user.epargneCumulee());
            return questionObs.pipe(
              map((question) => (question.question ? question : createEndParcoursQuestion(question))),
              tapResponse({
                next: (question) => question,
                error: (e) => errors.addError(e as Error, `Impossible de récupérer les détails de la question`)
              })
            );
          }
        });
      },

      getResponse: (codeQuestion: string) =>
        computed(() => {
          return store.questionsReponses()?.find((qr) => qr.questionCode === codeQuestion);
        }),

      setTypeParcours(typeParcours: TypeParcours): void {
        patchState(store, () => ({ typeParcours }));
      },

      returnToFirstQuestion(): void {
        patchState(store, (state) => ({
          questionsReponses: (state.questionsReponses ?? []).map((questRes) => {
            return { ...questRes, current: !questRes.questionPrecedenteCode };
          })
        }));
      }
    })
  ),
  withHooks({
    onInit(store) {
      const localStorageService = inject(LocalStorageService);

      const savedState = localStorageService.getSavedState<QuestionnaireState>(StateKey.QUESTIONNAIRE);
      if (savedState) store.retrieveState(savedState);
      effect(() => {
        const state: QuestionnaireState = getState(store);
        localStorageService.saveState(StateKey.QUESTIONNAIRE, state);
      });
    }
  })
);

const changeIndexInState = (state: QuestionnaireState, newIndex: number) => {
  return {
    affichageInfos: {
      ...state.affichageInfos,
      current: newIndex
    }
  };
};

/**
 * génére la question "finale" qui permet à l'utilisateur d'afficher des pages d'explications ou non
 * @param sourceQuestion question précédente
 * @returns question finale
 */
export function createEndParcoursQuestion(sourceQuestion: QuestionDTO): QuestionDTO {
  return {
    code: CODE_FINAL_QUESTION,
    question: null,
    reponses: sourceQuestion.parcoursTypes.map((parcoursType) => {
      return {
        id: "",
        valeur: "",
        questionSuivante: "",
        ...parcoursType,
        libelle: ""
      };
    }),
    titre: null,
    valeur: 0,
    parcoursTypes: []
  };
}
